import { useState, useEffect, useCallback } from 'react';
import { validateApiResponse } from '../../lib/validateApiResponse';

/**
 * Unterlagen im Sales-Bereich — die Praesentationen, die diesem Partner gehoeren.
 *
 * Die Liste kam frueher aus einer festen Konstante im Client und war damit fuer
 * jeden Sales-Partner dieselbe: Markus sah den Engelmann AI Hub, der ihn nichts
 * angeht, und die Engelmanns haetten ihn verloren, sobald jemand ihn dort
 * herausnimmt. Jetzt entscheidet users.json pro Nutzer (`salesDecks`), der
 * Server liefert Namen und ob die Datei ueberhaupt da ist.
 */

const API = '/api/partner';

interface SalesDeck {
  file: string;
  label: string;
  zweck: string;
  rubrik: string;
  warnung?: string;
  exists: boolean;
  pdf?: string;
}

interface Rubrik {
  id: string;
  titel: string;
  hinweis: string;
}

export default function SalesMaterialsPanel() {
  const [decks, setDecks] = useState<SalesDeck[]>([]);
  const [rubriken, setRubriken] = useState<Rubrik[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [openFile, setOpenFile] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/sales/area`, { signal: AbortSignal.timeout(15000) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const raw = await res.json();
      const list = (raw as { decks?: unknown[] }).decks;
      if (!Array.isArray(list)) throw new Error('API /api/partner/sales/area: erwartet { decks: [] }');
      setDecks(list.map(item =>
        validateApiResponse<SalesDeck>(item, '/api/partner/sales/area.decks[item]', {
          file: 'string', label: 'string', zweck: 'string', rubrik: 'string', exists: 'boolean',
          warnung: { type: 'string', optional: true },
          pdf: { type: 'string', optional: true },
        }),
      ));
      const rub = (raw as { rubriken?: unknown[] }).rubriken;
      if (!Array.isArray(rub)) throw new Error('API /api/partner/sales/area: erwartet { rubriken: [] }');
      setRubriken(rub.map(item =>
        validateApiResponse<Rubrik>(item, '/api/partner/sales/area.rubriken[item]', {
          id: 'string', titel: 'string', hinweis: 'string',
        }),
      ));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (openFile) {
    const current = decks.find(d => d.file === openFile);
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px',
          borderBottom: '1px solid var(--tn-border)', flexShrink: 0,
        }}>
          <button
            data-ai-id="sales-materials-back"
            onClick={() => setOpenFile(null)}
            style={{
              padding: '6px 12px', borderRadius: 5, fontSize: 13, cursor: 'pointer',
              background: 'var(--tn-surface)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)',
            }}
          >
            ← Übersicht
          </button>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tn-text)' }}>{current?.label}</span>
          <div style={{ flex: 1 }} />
          <a
            data-ai-id="sales-materials-open-new-tab"
            href={`/partner-decks/${openFile}`}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 12, color: 'var(--tn-text-muted)', textDecoration: 'none' }}
          >
            In neuem Tab öffnen ↗
          </a>
        </div>
        {/* Inline-Viewer statt target="_blank": ein neuer Tab oeffnet sich im
            eingebetteten CUI-Webview nicht (Klick tut nichts). Die Decks kommen
            same-origin von /partner-decks/, also kein Rahmen-Problem. */}
        <iframe
          src={`/partner-decks/${openFile}`}
          title={current?.label ?? 'Unterlage'}
          allow="fullscreen"
          style={{ flex: 1, width: '100%', border: 'none', background: '#000' }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 20, overflow: 'auto', height: '100%' }}>
      {loading && <div style={{ color: 'var(--tn-text-muted)', fontSize: 13 }}>Lädt…</div>}

      {error && (
        <div style={{
          padding: 12, borderRadius: 6, background: 'rgba(247,118,142,0.12)',
          border: '1px solid var(--tnf-f7768e)', color: 'var(--tnf-f7768e)', fontSize: 13, marginBottom: 12,
        }}>
          Unterlagen konnten nicht geladen werden: {error}
        </div>
      )}

      {!loading && !error && decks.length === 0 && (
        <div style={{ color: 'var(--tn-text-muted)', fontSize: 13 }}>
          Für dich ist noch keine Unterlage freigegeben.
        </div>
      )}

      {rubriken.map(r => {
        const drin = decks.filter(d => d.rubrik === r.id);
        if (drin.length === 0) return null;
        return (
          <section key={r.id} style={{ marginBottom: 26 }} data-ai-id={`sales-materials-rubrik-${r.id}`}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tn-text)', marginBottom: 2 }}>
              {r.titel}
            </div>
            <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
              {r.hinweis}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 720 }}>
              {drin.map(d => {
                const istPdf = d.file.endsWith('.pdf');
                return (
                  <div
                    key={d.file}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 8,
                      background: 'var(--tn-surface)',
                      border: `1px solid ${d.warnung ? 'var(--tnf-e0af68)' : 'var(--tn-border)'}`,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: d.exists ? 'var(--tn-text)' : 'var(--tn-text-muted)' }}>
                        {d.label}
                      </div>
                      {d.zweck && (
                        <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginTop: 2, lineHeight: 1.5 }}>{d.zweck}</div>
                      )}
                      {d.warnung && (
                        <div style={{ fontSize: 12, color: 'var(--tnf-e0af68)', marginTop: 4, lineHeight: 1.5 }}>
                          {d.warnung}
                        </div>
                      )}
                      {!d.exists && (
                        <div style={{ fontSize: 11, color: 'var(--tnf-f7768e)', marginTop: 3 }}>
                          Datei liegt nicht auf dem Server — bitte Rafael Bescheid geben.
                        </div>
                      )}
                    </div>
                    {!istPdf && (
                      <button
                        data-ai-id={`sales-materials-select-${d.file}`}
                        onClick={() => d.exists && setOpenFile(d.file)}
                        disabled={!d.exists}
                        style={{
                          padding: '7px 14px', borderRadius: 5, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                          background: d.exists ? 'var(--tn-blue)' : 'var(--tn-surface)',
                          color: d.exists ? '#fff' : 'var(--tn-text-muted)',
                          border: '1px solid var(--tn-border)', cursor: d.exists ? 'pointer' : 'not-allowed',
                        }}
                      >
                        Ansehen
                      </button>
                    )}
                    {(d.pdf || istPdf) && d.exists && (
                      <a
                        data-ai-id={`sales-materials-pdf-${d.pdf ?? d.file}`}
                        href={`/partner-decks/${d.pdf ?? d.file}`}
                        download
                        style={{
                          padding: '7px 14px', borderRadius: 5, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                          background: 'var(--tn-surface)', color: 'var(--tn-text)',
                          border: '1px solid var(--tn-border)', textDecoration: 'none',
                        }}
                      >
                        PDF laden
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
